import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { InventoryItemStatuses } from '../enums/inventory-item-statuses.enum';
import { InventoryItemApiResponse } from '../models/inventory/inventory-item-api-response.interface';
import { InventoryItemAvailability } from '../models/inventory/inventory-item-availability.interface';
import { InventoryItemCreate } from '../models/inventory/inventory-item-create.interface';
import { InventoryItemChange } from '../models/inventory/inventory-item-change.interface';
import { InventoryItemCreateRequest } from '../models/inventory/inventory-item-create-request.interface';
import { InventoryItemFinanceUpdate } from '../models/inventory/inventory-item-finance-update.interface';
import { InventoryItemGeneralInfoUpdate } from '../models/inventory/inventory-item-general-info-update.interface';
import {
  InventoryItemComponent,
  InventoryItemComponentCreate,
  InventoryItemComponentUpdate,
} from '../models/inventory/inventory-item-component.model';
import { InventoryItem } from '../models/inventory/inventory-item.interface';
import { InventoryItemStockRecount } from '../models/inventory/inventory-item-stock-recount.interface';
import { InventoryItemUnitPricingUpdate } from '../models/inventory/inventory-item-unit-pricing-update.interface';
import { InventoryItemWarehouseUpdate } from '../models/inventory/inventory-item-warehouse-update.interface';
import { UnitOfMeasureLookup } from '../models/inventory/unit-of-measure-lookup.interface';
import { PagedList } from '../models/paged-list.interface';
import { RequestModel } from '../models/request-model.interface';

@Injectable()
export class InventoryItemService {
  private readonly httpClient = inject(HttpClient);


  /**
   * Returns paged inventory items for a company. The backend hides archived items by default;
   * pass a `status` filter with `archived` or `all` in `request.filters` to include them.
   */
  public getCompanyInventoryItemsPaged(
    companyId: string,
    request: RequestModel,
  ): Observable<PagedList<InventoryItem>> {
    return this.httpClient.post<PagedList<InventoryItem>>(`${this.getInventoryItemsUrl(companyId)}/company-items`, request);
  }

  public getInventoryItem(companyId: string, itemId: string): Observable<InventoryItem> {
    return this.httpClient
      .get<InventoryItemApiResponse>(`${this.getInventoryItemsUrl(companyId)}/${itemId}`)
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public getInventoryItemChanges(companyId: string, itemId: string): Observable<InventoryItemChange[]> {
    return this.httpClient.get<InventoryItemChange[]>(`${this.getInventoryItemsUrl(companyId)}/${itemId}/changes`);
  }

  public getUnitsOfMeasureLookup(companyId: string): Observable<UnitOfMeasureLookup[]> {
    return this.httpClient.get<UnitOfMeasureLookup[]>(`${this.getInventoryItemsUrl(companyId)}/units-of-measure-lookup`);
  }

  public getInventoryItemAvailability(companyId: string, itemId: string): Observable<InventoryItemAvailability> {
    return this.httpClient.get<InventoryItemAvailability>(
      `${this.getInventoryItemsUrl(companyId)}/${itemId}/availability`,
    );
  }

  public createInventoryItem(companyId: string, model: InventoryItemCreate): Observable<InventoryItem> {
    return this.httpClient
      .post<InventoryItemApiResponse>(this.getInventoryItemsUrl(companyId), this.toCreateRequest(model))
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public updateInventoryItemGeneralInfo(
    companyId: string,
    itemId: string,
    changes: InventoryItemGeneralInfoUpdate,
  ): Observable<InventoryItem> {
    return this.httpClient
      .patch<InventoryItemApiResponse>(`${this.getInventoryItemsUrl(companyId)}/${itemId}/general-info`, changes)
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public updateInventoryItemFinance(
    companyId: string,
    itemId: string,
    changes: InventoryItemFinanceUpdate,
  ): Observable<InventoryItem> {
    return this.httpClient
      .patch<InventoryItemApiResponse>(`${this.getInventoryItemsUrl(companyId)}/${itemId}/finance`, changes)
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public updateInventoryItemWarehouse(
    companyId: string,
    itemId: string,
    changes: InventoryItemWarehouseUpdate,
  ): Observable<InventoryItem> {
    return this.httpClient
      .patch<InventoryItemApiResponse>(`${this.getInventoryItemsUrl(companyId)}/${itemId}/warehouse`, changes)
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public updateInventoryItemUnitPricing(
    companyId: string,
    itemId: string,
    payload: InventoryItemUnitPricingUpdate,
  ): Observable<InventoryItem> {
    return this.httpClient
      .put<InventoryItemApiResponse>(`${this.getInventoryItemsUrl(companyId)}/${itemId}/unit-pricing`, payload)
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public archiveInventoryItem(companyId: string, itemId: string): Observable<InventoryItem> {
    return this.httpClient
      .put<InventoryItemApiResponse>(`${this.getInventoryItemsUrl(companyId)}/${itemId}/archive`, null)
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public unarchiveInventoryItem(companyId: string, itemId: string): Observable<InventoryItem> {
    return this.httpClient
      .put<InventoryItemApiResponse>(`${this.getInventoryItemsUrl(companyId)}/${itemId}/unarchive`, null)
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public getComponents(companyId: string, itemId: string): Observable<InventoryItemComponent[]> {
    return this.httpClient.get<InventoryItemComponent[]>(this.getComponentsUrl(companyId, itemId));
  }

  public addComponent(
    companyId: string,
    itemId: string,
    model: InventoryItemComponentCreate,
  ): Observable<InventoryItemComponent> {
    return this.httpClient.post<InventoryItemComponent>(this.getComponentsUrl(companyId, itemId), model);
  }

  public updateComponent(
    companyId: string,
    itemId: string,
    componentId: string,
    model: InventoryItemComponentUpdate,
  ): Observable<InventoryItemComponent> {
    return this.httpClient.put<InventoryItemComponent>(
      `${this.getComponentsUrl(companyId, itemId)}/${componentId}`,
      model,
    );
  }

  public deleteComponent(companyId: string, itemId: string, componentId: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.getComponentsUrl(companyId, itemId)}/${componentId}`,
    );
  }

  public setIsComposite(
    companyId: string,
    itemId: string,
    isComposite: boolean,
    expectedConcurrencyToken: string,
  ): Observable<InventoryItem> {
    return this.httpClient
      .patch<InventoryItemApiResponse>(
        `${this.getInventoryItemsUrl(companyId)}/${itemId}/is-composite`,
        { isComposite, expectedConcurrencyToken },
      )
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  public recountInventoryItemStock(
    companyId: string,
    itemId: string,
    payload: InventoryItemStockRecount,
  ): Observable<InventoryItem> {
    return this.httpClient
      .post<InventoryItemApiResponse>(`${this.getInventoryItemsUrl(companyId)}/${itemId}/stock-recounts`, payload)
      .pipe(map((response) => this.toInventoryItem(response)));
  }

  private toInventoryItem(response: InventoryItemApiResponse): InventoryItem {
    const baseUnitOfMeasureId = response.baseUnitOfMeasureId ?? response.unitOfMeasureId ?? null;
    const unit =
      response.unit ??
      response.unitsOfMeasure?.find((candidate) => candidate.unitOfMeasureId === baseUnitOfMeasureId)?.displayName;

    return {
      ...response,
      baseUnitOfMeasureId,
      unit,
    };
  }

  private toCreateRequest(model: InventoryItemCreate): InventoryItemCreateRequest {
    return {
      number: model.number.trim(),
      displayName: model.displayName.trim(),
      displayName2: this.toNullableTrimmedString(model.displayName2),
      goodsCategoryId: model.goodsCategoryId ?? null,
      gtin: this.toNullableTrimmedString(model.gtin),
      itemType: model.itemType,
      status: model.status ?? InventoryItemStatuses.Active,
      manufacturer: this.toNullableTrimmedString(model.manufacturer),
      model: this.toNullableTrimmedString(model.model),
      baseUnitOfMeasureId: model.baseUnitOfMeasureId,
      unitPrice: model.unitPrice,
      unitCost: model.unitCost,
      currencyCode: this.toNullableTrimmedString(model.currencyCode),
      priceIncludesTax: model.priceIncludesTax,
    };
  }

  private toNullableTrimmedString(value?: string | null): string | null {
    if (typeof value !== 'string') {
      return value ?? null;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : null;
  }

  private getInventoryItemsUrl(companyId: string): string {
    return `${environment.apiUrl}/companies/${companyId}/inventory-items`;
  }

  private getComponentsUrl(companyId: string, itemId: string): string {
    return `${this.getInventoryItemsUrl(companyId)}/${itemId}/components`;
  }
}
